import { Branding, BrandingService } from '../../../domain/service/brandingService';
import { AuthenticationService } from '../../../security/auth/authentication.service';
import { debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from '../../../domain/service/payment/payment.service';
import { ReturnDetail, ReturnUpdate, InstallmentPlan } from '../../../domain/model/payment/payment.model';
// Reusing the dashboard's basket sessionStorage key/constant rather than duplicating the
// literal — "Add to basket" here queues into the exact same basket its floating button reads.
import { PaymentsDashboardComponent } from '../payments-dashboard/payments-dashboard.component';
import { NativeIoService } from '../../../core/native/native-io.service';

@Component({
  selector: 'app-return-detail',
  templateUrl: './return-detail.component.html',
  styleUrls: ['./return-detail.component.css']
})
export class ReturnDetailComponent implements OnInit {
  loading = true;
  error: string | null = null;
  saving = false;

  ret: ReturnDetail | null = null;
  returnId!: number;

  plan: InstallmentPlan | null = null;
  showReturnGroup = false;

  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private route: ActivatedRoute,
    private router: Router,
    private nativeIo: NativeIoService,
    private branding: BrandingService,
    private auth: AuthenticationService
  ) {
    this.form = this.fb.group({ rows: this.fb.array([]) });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/app/payments']);
      return;
    }
    this.returnId = parseInt(id, 10);
    this.load();
    // Recalculate as the taxpayer types (QA 2026-09-30): late fee/interest come from the
    // server, so a pause in typing saves and refreshes the totals — no separate Calculate step.
    this.form.valueChanges.pipe(debounceTime(800), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => { if (this.form.dirty && this.form.valid && !this.saving) this.autoSave(); });
  }

  private readonly destroyRef = inject(DestroyRef);

  /** Save without rebuilding the rows, so the field being typed in keeps focus. */
  private autoSave(): void {
    this.saving = true;
    const body = this.buildUpdate();
    // Compare line values only: expectedVersion changes with every save, so including it
    // would make every response look like "typed more" and loop PUTs forever.
    const sent = JSON.stringify(body.properties);
    this.paymentService.updateReturn(this.returnId, body).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.ret = data;
        this.saving = false;
        // Typed more while this was in flight: save again rather than marking it saved.
        // this.ret now holds the server's new version, so the follow-up save sends it.
        if (JSON.stringify(this.buildUpdate().properties) === sent) this.form.markAsPristine(); else this.autoSave();
      },
      error: (err) => this.saveFailed(err)
    });
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getReturnDetail(this.returnId).subscribe({
      next: (data) => {
        this.applyDetail(data);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading return:', err);
        this.error = 'Failed to load the return. Please try again.';
        this.loading = false;
      }
    });

    // Installment plan is optional — silently ignore if this return isn't on a plan.
    this.paymentService.getInstallmentPlan(this.returnId).subscribe({
      next: (plan) => { this.plan = plan ?? null; },
      error: () => { this.plan = null; }
    });
  }

  get typeLabel(): string {
    if (this.plan?.currentSequenceNo && this.plan?.installmentCount) {
      return `Installment ${this.plan.currentSequenceNo}/${this.plan.installmentCount}`;
    }
    const type = this.ret?.type ?? '';
    return this.isSupplemental ? `${type} · Supplemental` : type;
  }

  get isSupplemental(): boolean { return this.ret?.origin === 'SUPPLEMENTAL'; }

  toggleReturnGroup(open: boolean): void {
    this.showReturnGroup = open;
  }

  private applyDetail(data: ReturnDetail): void {
    this.ret = data;
    this.rows.clear();
    // Shared return: a guest may edit only its own property's line; the rest show read-only.
    const editable = new Set(data.editableReturnPropertyIds ?? (data.properties ?? []).map(p => p.id));
    (data.properties ?? []).forEach(p => {
      const g = this.fb.group({
        id: [p.id],
        address: [p.address],
        gross: [p.grossRentalReceipts ?? 0],
        exemptions: [p.exemptions ?? 0]
      });
      if (!editable.has(p.id)) g.disable({ emitEvent: false });
      this.rows.push(g);
    });
    this.form.markAsPristine();
  }

  get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  // ---------- Live calculations (instant feedback; server is authoritative on save) ----------
  private num(v: any): number {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }

  /** Clear a lone "0" on focus so typing doesn't leave "0" stuck in front of the new digits. */
  get grossTotal(): number {
    return this.rows.controls.reduce((s, c) => s + this.num(c.get('gross')?.value), 0);
  }
  get exemptionTotal(): number {
    return this.rows.controls.reduce((s, c) => s + this.num(c.get('exemptions')?.value), 0);
  }
  /**
   * Assessment / lien / roll-up returns carry a fixed amount assessed by the collector and
   * have no editable property rows — deriving the tax from those (empty) rows showed $0.00
   * on the portal for a return the office had assessed. With no rows there is nothing to
   * edit, so the server's figures are simply the answer. The empty-rows check (not a
   * hardcoded list of return types) is the signal: it's exactly the condition under which
   * grossTotal/taxableTotal below would otherwise silently collapse to 0.
   */
  get isFixedAmount(): boolean {
    return this.rows.length === 0;
  }
  get taxableTotal(): number {
    return this.isFixedAmount
      ? (this.ret?.taxableRentalReceipts ?? 0)
      // Per line, clamped at 0 (matches the server): one line's exemptions never reduce another's tax.
      : this.rows.controls.reduce((s, c) =>
          s + Math.max(0, this.num(c.get('gross')?.value) - this.num(c.get('exemptions')?.value)), 0);
  }
  get taxRate(): number { return this.ret?.taxRate ?? 0; }
  get lateFee(): number { return this.ret?.lateFee ?? 0; }
  get lateInterest(): number { return this.ret?.lateInterest ?? 0; }
  get onlineCollectionAllowance(): number { return this.ret?.onlineCollectionAllowance ?? 0; }
  get totalTaxes(): number {
    return this.isFixedAmount ? (this.ret?.totalTaxes ?? 0) : this.taxableTotal * this.taxRate;
  }
  get totalAmountDue(): number {
    if (this.isFixedAmount) {
      return this.ret?.totalAmountDue ?? 0;
    }
    return Math.max(0, this.totalTaxes + this.lateFee + this.lateInterest - this.onlineCollectionAllowance);
  }

  statusClass(status?: string): string {
    switch ((status || '').toLowerCase()) {
      case 'paid':
      case 'completed': return 'is-success';
      case 'partially paid':
      case 'partially_paid': return 'is-info';
      case 'unpaid':
      case 'pending': return 'is-pending';
      case 'past due':
      case 'overdue':
      case 'past_due': return 'is-danger';
      default: return 'is-neutral';
    }
  }

  private buildUpdate(): ReturnUpdate {
    return {
      expectedVersion: this.ret?.version,
      properties: this.rows.controls.filter(c => c.enabled).map(c => ({
        id: c.get('id')?.value,
        grossRentalReceipts: this.num(c.get('gross')?.value),
        exemptions: this.num(c.get('exemptions')?.value)
      }))
    };
  }

  // ---------- Actions ----------
  goBack(): void {
    this.router.navigate(['/app/payments']);
  }

  private static readonly SAVING_NOTICE = 'Saving your changes — try again in a moment.';

  saveChanges(): void {
    if (this.saving) { this.notice = ReturnDetailComponent.SAVING_NOTICE; return; }
    this.saving = true;
    this.error = null;
    this.paymentService.updateReturn(this.returnId, this.buildUpdate()).subscribe({
      next: (data) => {
        this.applyDetail(data);
        this.saving = false;
      },
      error: (err) => this.saveFailed(err)
    });
  }

  /**
   * 409 "changed by someone else": the other account's save wins — reload (dropping our edits)
   * and say why. Other 409s (e.g. already paid beyond the new amount), 403 and 400 keep the page and
   * the edits so the taxpayer can correct them. Anything else replaces the page with Retry.
   */
  private saveFailed(err: any): void {
    console.error('Error saving return:', err);
    this.saving = false;
    const msg: string | undefined = err?.error?.message || err?.error?.error;
    // Match on the message, not the status: the portal controller maps IllegalStateException
    // to 422 and IllegalArgumentException to 404 (its own handlers), not 409/400.
    if (/changed by someone else/.test(msg ?? '')) {
      this.load();
      this.notice = msg + ' Your unsaved changes were discarded.';
      return;
    }
    // Autosave can send exemptions above gross mid-typing; keep the page and the edits.
    if ([400, 403, 404, 409, 422].includes(err?.status) && msg) {
      this.notice = msg || 'This change could not be saved.';
      return;
    }
    this.error = msg || 'Failed to save the return. Please try again.';
  }

  /** Persist edits (if any) then go to the payment basket flow. */
  private saveThen(navigate: () => void): void {
    if (this.form.pristine) { navigate(); return; }
    // Never two saves in flight with the same version (the second would 409).
    if (this.saving) { this.notice = ReturnDetailComponent.SAVING_NOTICE; return; }
    this.saving = true;
    this.error = null;
    this.paymentService.updateReturn(this.returnId, this.buildUpdate()).subscribe({
      next: (data) => {
        this.applyDetail(data);
        this.saving = false;
        navigate();
      },
      error: (err) => this.saveFailed(err)
    });
  }

  /**
   * Queue this return in the same basket the dashboard's floating "Basket" button reads, then
   * go back to the returns list so the taxpayer can keep adding more before checking out.
   * Previously this navigated straight to checkout with just this one return — identical to
   * submit() below, which is the "two buttons, one behavior" confusion Todd reported.
   */
  addToBasket(): void {
    this.notice = null;
    this.saveThen(() => {
      if ((this.totalAmountDue ?? 0) <= 0) {
        // Nothing to pay: a $0 return is FILED (Submit), never carted. Todd QA round 2.
        // (notice, not error: error replaces the whole page)
        this.notice = 'Nothing is due on this return, so it cannot be added to the cart. '
          + 'Enter your gross receipts, or use "Submit return" to file it as a zero return.';
        return;
      }
      this.afterOlderReturnsCheck(this.readBasket(), () => {
        this.queueInBasket(this.returnId);
        this.router.navigate(['/app/payments']);
      });
    });
  }

  /** Banner above the action bar for messages that must not hide the return. */
  notice: string | null = null;

  /**
   * Oldest-first rule, same as the dashboard's add button and the server's checkout guard:
   * an older past-due return with a balance must be paid first or be in the same cart.
   * This page's buttons skipped it, so a later month could be carted ahead of an unpaid one.
   */
  private afterOlderReturnsCheck(inCart: number[], go: () => void): void {
    const due = this.ret?.dueDate ? new Date(this.ret.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
    const now = Date.now();
    // Oldest-first is per home account: a shared return never blocks the caller's own, nor vice versa.
    const ownerKey = (x: { shared?: boolean; owningAccountId?: number } | null) => x?.shared ? x.owningAccountId : null;
    const mine = ownerKey(this.ret);
    this.paymentService.getOutstandingReturns().subscribe({
      next: (list) => {
        const blockers = list.filter(o =>
          o.id !== this.returnId && !inCart.includes(o.id) && (o.balanceDue ?? 0) > 0 &&
          ownerKey(o) === mine &&
          !!o.dueDate && new Date(o.dueDate).getTime() < now && new Date(o.dueDate).getTime() < due);
        if (blockers.length) {
          this.notice = 'Older past-due returns must be paid first: '
            + blockers.map(b => b.period).join(', ') + '. Add them to the cart before this one.';
          return;
        }
        go();
      },
      error: () => go(), // the server re-checks at checkout
    });
  }

  private readBasket(): number[] {
    try {
      const raw = sessionStorage.getItem(PaymentsDashboardComponent.BASKET_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private queueInBasket(id: number): void {
    try {
      const ids = this.readBasket();
      if (!ids.includes(id)) {
        ids.push(id);
        sessionStorage.setItem(PaymentsDashboardComponent.BASKET_KEY, JSON.stringify(ids));
      }
    } catch {
      /* sessionStorage unavailable — falls back to submit() for an immediate pay path */
    }
  }

  /** Pay this return right now: save, then go straight to checkout for just this return. */
  submit(): void {
    this.notice = null;
    const go = () =>
      this.router.navigate(['/app/payments/make-payment'], { queryParams: { returns: this.returnId } });
    // Paying just this return: anything older and past due blocks it. A $0 zero filing isn't a payment.
    this.saveThen(() => (this.totalAmountDue ?? 0) > 0 ? this.afterOlderReturnsCheck([], go) : go());
  }

  /** Pay the plan's next amount due (down payment or next installment) for this return. */
  payNextInstallment(): void {
    this.saveThen(() =>
      this.router.navigate(['/app/payments/make-payment'],
        { queryParams: { returns: this.returnId, installment: 'true' } }));
  }

  /** No print dialog in a WebView — the template hides the mail-in button there. */
  get canPrint(): boolean {
    return !this.nativeIo.isNative;
  }

  /**
   * Printed mail-in voucher in its own window. Printing the app page itself (window.print())
   * hung Chrome's print preview — the whole shell, sidenav and scroll containers went to the
   * printer (QA 2026-09-30). The window is opened synchronously so popup blockers allow it.
   */
  mailInPayment(): void {
    const win = window.open('', '_blank');
    if (!win) { this.error = 'Allow pop-ups for this site to print the mail-in form.'; return; }
    win.document.write('<p style="font-family:Arial;padding:24px">Preparing mail-in form…</p>');
    this.saveThen(() => this.branding.get().subscribe(b => this.writeVoucher(win, b)));
  }

  private writeVoucher(win: Window, b: Branding): void {
    const r = this.ret;
    if (!r) { win.close(); return; }
    const esc = (x: any) => String(x ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]!));
    const money = (n: number | null | undefined) => (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    const payee = b.certTaxCollectorName || b.taxCollectorName || 'County Tax Collector';
    const user = this.auth.getCurrentUser2();
    const rows = this.rows.controls.map(c =>
      `<tr><td>${esc(c.get('address')?.value)}</td><td class="n">${money(this.num(c.get('gross')?.value))}</td>`
      + `<td class="n">${money(this.num(c.get('exemptions')?.value))}</td></tr>`).join('');
    const line = (label: string, v: number, neg = false) =>
      `<tr><td>${label}</td><td class="n">${neg && v ? '-' : ''}${money(v)}</td></tr>`;
    win.document.open();
    win.document.write(`<!doctype html><html><head><title>Mail-in payment ${esc(r.period)}</title><style>
      body{font-family:Arial,sans-serif;font-size:12px;color:#000;max-width:680px;margin:32px auto}
      h1{font-size:18px;margin:0 0 4px}h2{font-size:13px;margin:20px 0 6px;text-transform:uppercase}
      table{width:100%;border-collapse:collapse}td,th{padding:5px 6px;border-bottom:1px solid #ddd;text-align:left}
      .n{text-align:right}.tot td{font-weight:700;border-top:2px solid #000}
      .box{border:1px solid #000;padding:12px;margin-top:20px}.muted{color:#555}
      .cut{border-top:1px dashed #000;margin:28px 0 12px;text-align:center;font-size:10px;color:#555}
    </style></head><body>
      <h1>Tourist Development Tax — Mail-in Payment</h1>
      <div class="muted">${esc(b.countyName || payee)}</div>
      <h2>Return</h2>
      <table><tr><th>Account</th><th>Period</th><th>Type</th><th>Due date</th></tr>
        <tr><td>${esc(user?.displayName || user?.printedName)} (#${esc(user?.accountId)})</td><td>${esc(r.period)}</td>
        <td>${esc(r.type)}</td><td>${esc(r.dueDate)}</td></tr></table>
      <h2>Properties</h2>
      <table><tr><th>Address</th><th class="n">Gross rental receipts</th><th class="n">Exemptions</th></tr>${rows}</table>
      <h2>Amount due</h2>
      <table>${line('Taxable rental receipts', this.taxableTotal)}${line('Total taxes', this.totalTaxes)}
        ${line('Late fee', this.lateFee)}${line('Late interest', this.lateInterest)}
        ${line('Online collection allowance (online filing only)', 0)}
        <tr class="tot"><td>Total amount due</td><td class="n">${money(this.totalAmountDue)}</td></tr></table>
      <div class="cut">✂ detach and return with your payment</div>
      <div class="box">
        <div>Make check or money order payable to: <strong>${esc(payee)}</strong></div>
        <div style="margin-top:8px">Mail to:<br><strong>${esc(payee)}</strong><br>${b.mailingAddress.map(esc).join('<br>')}</div>
        <div style="margin-top:8px">Account #${esc(user?.accountId)} · Period ${esc(r.period)} · Amount enclosed: <strong>${money(this.totalAmountDue)}</strong></div>
      </div>
      <script>window.onload = () => window.print();<\/script>
    </body></html>`);
    win.document.close();
  }
}
